import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CookieManager } from '../../utils/cookieManager';
import { TokenUtils } from '../../utils/tokenUtils';
import { useInitialization } from 'components/InitializationProvider';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiOrchestrator } from 'services/apiService';
import { handleHttpError } from 'utils/errorHandler';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

interface LoginSuccessData {
  access_token: string;
  user_id: string;
  isPasswordReset?: boolean;
  expires_in?: number;
}

const loginSchema = yup.object().shape({
  email: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  password: yup
    .string()
    .required('Senha é obrigatória')
    .min(8, 'A senha deve ter no mínimo 8 caracteres'),
});

const useLogin = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });
  const { initializeUserContext, logout } = useUser();
  const { toastError, toastInfo } = useToast();
  const { shouldShowLoader } = useInitialization();
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState<string | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [email, setEmail] = useState<string>('');

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const handleLoginSuccess = async (data: LoginSuccessData) => {
    try {
      console.log('[Login] Iniciando processamento do login:', {
        hasAccessToken: !!data.access_token,
        hasUserId: !!data.user_id,
        userId: data.user_id,
      });

      if (!data.access_token || !data.user_id) {
        console.error('[Login] Token ou user_id não encontrados na resposta:', data);
        toastError('Erro ao fazer login: dados inválidos');
        return;
      }

      // Salvar access token no cookie também
      CookieManager.setAccessToken(data.access_token);

      // Calcular e salvar expiração se disponível
      if (data.expires_in) {
        const expiryTimestamp = TokenUtils.generateExpiryTimestamp(data.expires_in);
        CookieManager.setTokenExpiry(expiryTimestamp);
        console.log('[Login] Expiração do token definida:', new Date(expiryTimestamp));
      }

      // Configurar token e userId
      console.log('[Login] Chamando initializeUserContext...');
      await initializeUserContext(data.user_id, data.access_token);
      console.log('[Login] Login processado com sucesso!');
    } catch (error) {
      console.error('[Login] Erro ao processar login:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Erro desconhecido',
        stack: error instanceof Error ? error.stack : undefined,
      });
      toastError('Erro ao processar login. Por favor, tente novamente.');
      logout();
    }
  };

  const onSubmit = async (data: { email: string; password: string }) => {
    try {
      // Save the email to state so it can be used in the ChangePasswordModal
      setEmail(data.email);
      console.log('[Login] 🔧 Iniciando login...');

      // Só mostra loading local se não estiver no sistema centralizado
      if (!shouldShowLoader) {
        setLoading(true);
      }

      const response = await apiOrchestrator
        .post('/auth/login', {
          email: data.email,
          password: data.password,
        })
        .catch(error => {
          console.error('[Login] ❌ Erro detalhado na requisição:', {
            error,
            message: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            headers: error.response?.headers,
            data: error.response?.data,
            config: {
              url: error.config?.url,
              method: error.config?.method,
              withCredentials: error.config?.withCredentials,
              headers: error.config?.headers,
            },
            request: {
              readyState: error.request?.readyState,
              status: error.request?.status,
              responseText: error.request?.responseText,
            },
          });
          throw error;
        });

      console.log('[Login] ✅ Resposta do login recebida:', {
        status: response.status,
        hasData: !!response.data,
      });

      // Log simples - cookies HttpOnly são gerenciados automaticamente pelo navegador
      console.log(
        '[Login] ✅ Login realizado com sucesso - cookies HttpOnly serão enviados automaticamente pelo navegador'
      );

      // Capturar headers
      let cognitoUserPool = response.headers['x-cognito-userpool'];
      const organizationUUID = response.headers['x-organization-uuid'];

      if (!response.data) {
        throw new Error('Resposta do servidor inválida');
      }

      // Fallback para Cognito User Pool via .env se não vier no header (devido a CORS)
      if (!cognitoUserPool) {
        console.warn(
          'Cognito User Pool não encontrado nos headers (verificar CORS/Access-Control-Expose-Headers). Usando fallback do .env...'
        );
        cognitoUserPool = process.env.REACT_APP_URL_AWS_COGNITO_USER_POOL;
      }

      // Validar headers (Organization UUID AINDA precisa vir do header)
      if (!cognitoUserPool || !organizationUUID) {
        console.error('Headers necessários não encontrados:', {
          cognitoUserPool: !!cognitoUserPool,
          organizationUUID: !!organizationUUID,
        });
        throw new Error(
          'Headers necessários não encontrados (Organization UUID precisa vir da resposta, Cognito Pool pode usar fallback do .env)'
        );
      }

      // Salvar headers no localStorage
      localStorage.setItem('x-cognito-userpool', cognitoUserPool);
      localStorage.setItem('x-organization-uuid', organizationUUID);

      console.log('[Login] Headers salvos no localStorage:', {
        cognitoUserPool,
        organizationUUID,
      });

      // Cookies HttpOnly são gerenciados automaticamente pelo navegador
      console.log('[Login] ✅ Headers salvos - cookies HttpOnly serão enviados automaticamente');

      if (response.data.status === 'new_password_required') {
        setSession(response.data.session);
        toastInfo('É necessário redefinir sua senha.');
        setShowChangePasswordModal(true);
      } else {
        await handleLoginSuccess(response.data);
      }
    } catch (error: any) {
      console.error('[Login] Erro no login:', {
        error,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });

      if (error.response?.status === 401) {
        toastError('Email ou senha inválidos');
      } else if (error.response?.status === 403) {
        toastError('Usuário bloqueado. Por favor, contate o suporte.');
      } else {
        handleHttpError(error);
        toastError('Erro ao fazer login. Por favor, tente novamente.');
      }
    } finally {
      // Só para loading local se não estiver no sistema centralizado
      if (!shouldShowLoader) {
        setLoading(false);
      }
    }
  };

  return {
    control,
    handleSubmit,
    errors,
    loading,
    onSubmit,
    session,
    showChangePasswordModal,
    setShowChangePasswordModal,
    email,
    isMobile,
    isTablet,
    handleLoginSuccess,
  };
};

export default useLogin;
